export const V3_DENSITY_PRESETS = [
  { key: 'brief', label: 'Breve', min: 15, max: 30, target: 24 },
  { key: '30-50', label: '30–50', min: 30, max: 50, target: 42 },
  { key: '50-80', label: '50–80', min: 50, max: 80, target: 66 },
  { key: '80-100', label: '80–100', min: 80, max: 100, target: 90 },
];

const PARAGRAPH_SAMPLES = {
  brief: 'Un ritratto efficace nasce quando la persona smette di pensare alla macchina fotografica e comincia a sentirsi davvero presente davanti a chi la osserva.',
  '30-50': 'Un ritratto efficace nasce quando la persona smette di pensare alla macchina fotografica e comincia a sentirsi davvero presente. Il compito del fotografo non è imporre una posa perfetta, ma costruire uno spazio abbastanza sicuro perché postura, sguardo e piccoli gesti possano diventare autentici.',
  '50-80': 'Un ritratto efficace nasce quando la persona smette di pensare alla macchina fotografica e comincia a sentirsi davvero presente. Il compito del fotografo non è imporre una posa perfetta, ma costruire uno spazio abbastanza sicuro perché postura, sguardo e piccoli gesti possano diventare autentici. Per questo osservare viene prima di dirigere: ascoltare il ritmo della persona permette di capire quando intervenire, quando aspettare e soprattutto quando scattare.',
  '80-100': 'Un ritratto efficace nasce quando la persona smette di pensare alla macchina fotografica e comincia a sentirsi davvero presente. Il compito del fotografo non è imporre una posa perfetta, ma costruire uno spazio abbastanza sicuro perché postura, sguardo e piccoli gesti possano diventare autentici. Per questo osservare viene prima di dirigere: ascoltare il ritmo della persona permette di capire quando intervenire, quando aspettare e soprattutto quando scattare. La tecnica deve essere già sotto controllo, quasi invisibile, perché ogni interruzione sposta l’attenzione dalla relazione agli strumenti. Quando invece il dialogo resta continuo, il ritratto comincia a raccontare qualcosa che appartiene davvero a quella persona.',
};

export const V3_PREVIEW_COPY = Object.fromEntries(V3_DENSITY_PRESETS.map(preset => [preset.key, {
  h1: preset.key === '80-100' ? 'Il tuo ritratto racconta davvero chi sei quando nessuno ti sta chiedendo di posare?' : 'Il tuo ritratto racconta davvero chi sei?',
  h2: 'La differenza non è nella posa, ma nella relazione.',
  h3: 'OSSERVA PRIMA',
  paragraph: PARAGRAPH_SAMPLES[preset.key],
  cta: 'Scopri il metodo',
  client_name: 'CLIENT NAME',
  brand_name: 'BRAND NAME',
}]));

const SEMANTIC_FIELDS = ['h1', 'h2', 'h3', 'paragraph', 'cta'];

export function countWords(value) {
  return String(value || '').trim().split(/\s+/).filter(Boolean).length;
}

export function densityPresetForWords(words) {
  const n = Math.max(0, Number(words) || 0);
  return V3_DENSITY_PRESETS.find(item => n <= item.max)?.key || '80-100';
}

function roleSemantic(template, block) {
  const role = block?.role || 'paragraph';
  const fallback = { title: 'h1', subtitle: 'h2', eyebrow: 'h3', paragraph: 'paragraph', quote: 'paragraph', cta: 'cta', label: 'client_name', footer: 'brand_name', h1: 'h1', h2: 'h2', h3: 'h3' };
  const map = template?.builder_style?.semantic_map || {};
  return map[role] || fallback[role] || role;
}

function effectiveRoleStyle(template, semantic) {
  const builder = template?.builder_style || {};
  const sourceSlot = template?.source_slot || '';
  const global = builder?.global_roles?.[semantic] || {};
  const local = sourceSlot ? builder?.archetype_overrides?.[sourceSlot]?.roles?.[semantic] || {} : {};
  return { ...global, ...local };
}

function emptyRoleCapacity(semantic) {
  return { semantic, min_words: 0, max_words: 0, recommended_max_words: 0, source: 'role_missing' };
}

export function estimateTemplateRoleCapacity(template, semantic) {
  const layout = template?.layout || {};
  const canvas = layout.canvas || { width: 1080, height: 1350 };
  const width = Number(canvas.width || 1080);
  const height = Number(canvas.height || 1350);
  const blocks = Array.isArray(layout.text_blocks) ? layout.text_blocks : [];
  const candidates = blocks.filter(block => roleSemantic(template, block) === semantic);
  const block = [...candidates].sort((a, b) => Number(b.width || 0) * Number(b.height || 0) - Number(a.width || 0) * Number(a.height || 0))[0];
  if (!block) return emptyRoleCapacity(semantic);

  const style = effectiveRoleStyle(template, semantic);
  const fallbackSize = semantic === 'h1' ? 72 : semantic === 'h2' ? 38 : semantic === 'h3' ? 26 : semantic === 'cta' ? 30 : 30;
  const requested = Math.max(6, Number(style.font_size_px || block.font_size_max || block.font_size_min || fallbackSize));
  const lineHeight = Math.max(.7, Number(style.line_height || block.line_height || 1.25));
  const minRatio = Math.max(.35, Math.min(1, Number(layout?.overflow_policy?.min_font_size_ratio || .6)));
  const shrink = String(block.wrapping || 'natural') !== 'width_driven';
  const effectiveFont = requested * (shrink ? minRatio : 1);
  const boxWidth = Math.max(1, Number(block.width || 0) * width);
  const authoredHeight = Math.max(1, Number(block.height || 0) * height);
  const y = Math.max(0, Number(block.y || 0) * height);
  const boxHeight = shrink ? authoredHeight : Math.max(authoredHeight, height - y);
  const charsPerLine = boxWidth / Math.max(1, effectiveFont * .52);
  const lines = boxHeight / Math.max(1, effectiveFont * lineHeight);
  const safety = semantic === 'paragraph' ? .82 : .72;
  const words = Math.max(1, Math.floor((charsPerLine * lines * safety) / 6));
  const cap = semantic === 'paragraph' ? 160 : semantic === 'h1' ? 24 : semantic === 'h2' ? 28 : semantic === 'h3' ? 18 : 20;
  const recommended = Math.min(cap, words);
  const min = Math.max(1, Math.floor(recommended * .65));
  return { semantic, min_words: min, max_words: recommended, recommended_max_words: recommended, source: 'layout_estimate_v2' };
}

export function estimateTemplateContentCapacity(template) {
  const roles = Object.fromEntries(SEMANTIC_FIELDS.map(semantic => [semantic, estimateTemplateRoleCapacity(template, semantic)]));
  const paragraph = roles.paragraph;
  return {
    version: 2,
    roles,
    min_words: paragraph.min_words,
    max_words: paragraph.max_words,
    recommended_max_words: paragraph.recommended_max_words,
    preset: densityPresetForWords(paragraph.recommended_max_words),
    source: 'layout_estimate_v2',
  };
}

export function estimateTemplateParagraphCapacity(template) {
  return estimateTemplateContentCapacity(template);
}

export function templateCapacityProfile(template) {
  const saved = template?.builder_style?.content_capacity;
  if (saved?.roles && Object.keys(saved.roles).length) return saved;
  return estimateTemplateContentCapacity(template);
}

export function adjustedCapacity(profile, override = {}) {
  const base = Math.max(0, Number(profile?.recommended_max_words || profile?.max_words || 0));
  if (!base) return 0;
  const fontScale = Math.max(.65, Math.min(1, Number(override.font_scale || 1)));
  const lineScale = Math.max(.8, Math.min(1, Number(override.line_height_scale || 1)));
  return Math.min(180, Math.round(base / (fontScale * fontScale * lineScale)));
}

function presentSemantics(template) {
  const blocks = Array.isArray(template?.layout?.text_blocks) ? template.layout.text_blocks : [];
  return new Set(blocks.map(block => roleSemantic(template, block)));
}

function roleResult(words, max) {
  const used = Math.max(0, Number(words) || 0);
  const capacity = Math.max(0, Number(max) || 0);
  const state = !capacity || !used || used <= capacity ? 'fit' : 'overflow';
  return { state, words: used, max: capacity, excess: state === 'overflow' ? Math.max(0, used - capacity) : 0 };
}

function applyFallbackCapacity(roles, words, profile, override, template, primary, fallback) {
  const present = presentSemantics(template);
  if (!present.has(primary) || present.has(fallback) || !words[fallback]) return;

  const primaryCapacity = adjustedCapacity(profile?.roles?.[primary] || emptyRoleCapacity(primary), override);
  if (!primaryCapacity) return;

  const primaryWords = words[primary] || 0;
  const fallbackWords = words[fallback] || 0;
  if (primaryWords + fallbackWords <= primaryCapacity) {
    roles[primary] = roleResult(primaryWords, primaryCapacity);
    roles[fallback] = roleResult(fallbackWords, 0);
    return;
  }

  if (primaryWords <= primaryCapacity) {
    const remainingForFallback = Math.max(1, primaryCapacity - primaryWords);
    roles[primary] = roleResult(primaryWords, primaryCapacity);
    roles[fallback] = roleResult(fallbackWords, remainingForFallback);
    return;
  }

  roles[primary] = roleResult(primaryWords, primaryCapacity);
  roles[fallback] = roleResult(fallbackWords, 0);
}

export function capacityStatus(slide, template, override = {}) {
  if (!template) return { state: 'unassigned', roles: {}, overflow_roles: [] };
  const profile = templateCapacityProfile(template);
  const words = Object.fromEntries(SEMANTIC_FIELDS.map(field => [field, countWords(slide?.[field])]));
  const roles = {};

  for (const field of SEMANTIC_FIELDS) {
    const roleProfile = profile?.roles?.[field] || emptyRoleCapacity(field);
    roles[field] = roleResult(words[field], adjustedCapacity(roleProfile, override));
  }

  applyFallbackCapacity(roles, words, profile, override, template, 'h1', 'h2');
  applyFallbackCapacity(roles, words, profile, override, template, 'paragraph', 'h3');

  const overflowRoles = SEMANTIC_FIELDS.filter(field => roles[field]?.state === 'overflow');
  return { state: overflowRoles.length ? 'overflow' : 'fit', roles, overflow_roles: overflowRoles, profile };
}

export function splitParagraphAtSentenceBoundary(value) {
  const text = String(value || '').trim();
  if (!text) return ['', ''];
  const sentences = text.match(/[^.!?]+[.!?]+|[^.!?]+$/g)?.map(item => item.trim()).filter(Boolean) || [text];
  if (sentences.length < 2) {
    const words = text.split(/\s+/).filter(Boolean);
    const pivot = Math.max(1, Math.floor(words.length / 2));
    return [words.slice(0, pivot).join(' '), words.slice(pivot).join(' ')];
  }
  const total = sentences.reduce((sum, sentence) => sum + countWords(sentence), 0);
  let running = 0;
  let pivot = 1;
  for (let i = 0; i < sentences.length - 1; i += 1) {
    running += countWords(sentences[i]);
    pivot = i + 1;
    if (running >= total / 2) break;
  }
  return [sentences.slice(0, pivot).join(' '), sentences.slice(pivot).join(' ')];
}
